import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  buildServiceReport,
  downloadReportHtml,
  downloadReportPdf,
  ranked,
  countryLabel,
  REPORT_PALETTE,
  type ServiceReport,
} from '../../utils/serviceReport';
import { Spinner } from '../common/Spinner';

/** Horizontal proportional bar list — a dependency-free "chart". */
const BarList: React.FC<{
  data: [string, number][];
  labelFn?: (k: string) => string;
  limit?: number;
}> = ({ data, labelFn, limit = 8 }) => {
  const rows = data.slice(0, limit);
  const max = Math.max(1, ...rows.map(([, v]) => v));
  if (rows.length === 0) return <p className="text-xs text-gray-400">—</p>;
  return (
    <div className="space-y-1.5">
      {rows.map(([key, value], i) => (
        <div key={key} className="flex items-center gap-2">
          <span className="w-24 shrink-0 text-xs text-gray-600 truncate" title={labelFn ? labelFn(key) : key}>
            {labelFn ? labelFn(key) : key}
          </span>
          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 rounded-full"
              style={{
                width: `${(value / max) * 100}%`,
                backgroundColor: REPORT_PALETTE[i % REPORT_PALETTE.length],
              }}
            />
          </div>
          <span className="w-8 shrink-0 text-right text-xs font-medium text-gray-700">{value}</span>
        </div>
      ))}
    </div>
  );
};

const Metric: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5">
    <div className="text-xl font-bold text-gray-900">{value}</div>
    <div className="text-[11px] text-gray-500 mt-0.5">{label}</div>
  </div>
);

const ServiceReportSection: React.FC = () => {
  const { t } = useTranslation(['admin']);
  const [report, setReport] = useState<ServiceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await buildServiceReport());
    } catch (e: any) {
      setError(e?.message || 'Failed to build report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-gray-900">
          {t('admin:report.title', '서비스 전체 리포트')}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('admin:report.refresh', '새로고침')}
          </button>
          <button
            type="button"
            onClick={() => report && downloadReportHtml(report)}
            disabled={!report}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {t('admin:report.downloadHtml', 'HTML 다운로드')}
          </button>
          <button
            type="button"
            onClick={() => report && downloadReportPdf(report)}
            disabled={!report}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {t('admin:report.downloadPdf', 'PDF 다운로드')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center bg-white rounded-xl border border-gray-200">
          <Spinner size="medium" />
        </div>
      ) : error ? (
        <div className="py-10 text-center text-sm text-red-600 bg-white rounded-xl border border-gray-200">
          {error}
        </div>
      ) : report ? (
        <div className="space-y-6">
          {/* Key metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Metric label={t('admin:report.totalUsers', '전체 사용자')} value={report.users.total} />
            <Metric label={t('admin:report.influencers', '인플루언서')} value={report.influencers.profiles} />
            <Metric label={t('admin:report.campaigns', '캠페인')} value={report.campaigns.total} />
            <Metric label={t('admin:report.applications', '지원')} value={report.matches.total} />
            <Metric label={t('admin:report.newUsersToday', '오늘 가입')} value={report.users.newToday} />
            <Metric label={t('admin:report.appliedToday', '오늘 지원')} value={report.matches.appliedToday} />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {t('admin:report.usersByType', '사용자 유형별')}
              </h3>
              <BarList data={ranked(report.users.byType)} />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {t('admin:report.campByStatus', '캠페인 상태별')}
              </h3>
              <BarList data={ranked(report.campaigns.byStatus)} />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {t('admin:report.campByCountry', '캠페인 방문 국가별')}
              </h3>
              <BarList data={ranked(report.campaigns.byVisitCountry)} labelFn={countryLabel} />
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {t('admin:report.matchByStatus', '지원 상태별 (퍼널)')}
              </h3>
              <BarList data={ranked(report.matches.byStatus)} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ServiceReportSection;
